import { useState } from 'react'
import { X, Plus, Edit2, Trash2 } from 'lucide-react'

// Add Button Component
export const AddButton = ({ onClick, label = "Add New" }) => (
  <button
    onClick={onClick}
    className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded-lg flex items-center space-x-2 shadow-lg transition-all hover:shadow-blue-500/50"
  >
    <Plus className="w-5 h-5" />
    <span>{label}</span>
  </button>
);

// Action Controls for Edit/Delete
export const ActionControls = ({ onEdit, onDelete, size = "sm" }) => (
  <div className="flex space-x-2">
    <button
      onClick={onEdit}
      className={`text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 p-2 rounded transition-all ${size === 'lg' ? 'text-base' : 'text-sm'}`}
      title="Edit"
    >
      <Edit2 className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
    </button>
    <button
      onClick={onDelete}
      className={`text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2 rounded transition-all ${size === 'lg' ? 'text-base' : 'text-sm'}`}
      title="Delete"
    >
      <Trash2 className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
    </button>
  </div>
);

// Modal Component for Add/Edit Forms
export const Modal = ({ isOpen, onClose, title, children, onSubmit, submitLabel = "Save" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-blue-500/20 flex-shrink-0">
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-grow p-6">
          {children}
        </div>

        {/* Actions */}
        <div className="flex space-x-3 justify-end p-6 border-t border-blue-500/20 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors font-bold"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Confirmation Dialog for Delete
export const ConfirmDialog = ({ isOpen, onConfirm, onCancel, title = "Confirm Delete", message = "Are you sure?" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/30 p-6 w-full max-w-sm shadow-2xl">
        <h2 className="text-xl font-bold text-white mb-2">{title}</h2>
        <p className="text-gray-300 mb-6">{message}</p>
        <div className="flex space-x-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors font-bold"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Form Input Component
export const FormInput = ({ label, value, onChange, type = "text", placeholder = "", required = false }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-blue-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors"
    />
  </div>
);

// Form Select Component
export const FormSelect = ({ label, value, onChange, options = [], required = false }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-blue-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <select
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-400 transition-colors"
    >
      <option value="">Select {label}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// Form Textarea Component
export const FormTextarea = ({ label, value, onChange, placeholder = "", rows = 3, required = false }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-blue-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      required={required}
      className="w-full px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 transition-colors resize-none"
    />
  </div>
);
